import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';

import {DialogService} from '@app/common/services/dialog.service';
import {ConfirmationComponent} from '@app/common/components/confirmation/confirmation.component';
import {ClassName} from '@app/common/enums/class-name.enum';
import {AuthService} from '@app/common/services/auth.service';
import {UploadFileExtended} from '@app/common/components/fileupload/fileupload.component';
import {ValidationUtils} from "@app/doss/utils/validation.utils";
import {DokumentTypePipe} from "@app/doss/pipes/file-to-upload-type.pipe";
import {DokumentAdditionalDataModel} from "@app/doss/models/dokument-additional-data.model";
import {AttachmentModel} from "@app/common/models/attachmentModel";
import {
  DisabledAttachmentType,
  AttachmentChooseTypeFormComponent
} from "@app/common/components/attachment-choose-type-form/attachment-choose-type-form.component";
import {AttachmentTypeModel} from "@app/common/models/attachment-type.model";
import {AttachmentEditFormComponent} from "@app/common/components/attachment-edit-form/attachment-edit-form.component";

@Component({
  selector: 'ukon-status-documents',
  templateUrl: './ukon-status-documents.component.html',
  styleUrls: ['./ukon-status-documents.component.scss']
})
export class UkonStatusDocumentsComponent implements OnInit {
  // bindings
  @Input() defaultData: any = {};
  @Input() defaultAttachmentType: AttachmentTypeModel;
  @Input() dokumenty: File[] = [];
  @Input() dokumentyAdditionalData: DokumentAdditionalDataModel[] = [];
  @Input() allowType: boolean;
  @Input() maxHeight: number;
  @Input() authorizedUser: boolean;
  @Input() draggable = true;
  @Input() required = true;
  @Input() attachmentCategory = 'doss';
  @Input() disabledAttachmentTypes: DisabledAttachmentType[];
  @Output() updated = new EventEmitter<boolean>();

  // model
  restBaseUrl: string;
  authToken: string;

  userId: number;
  referentId: number;

  public readonly ValidationUtils = ValidationUtils;

  constructor(private authService: AuthService,
              private dialogService: DialogService,
              private fileToUploadTypePipe: DokumentTypePipe) {
    this.onFileSelect = this.onFileSelect.bind(this);
    this.onEditAttachment = this.onEditAttachment.bind(this);
    this.onDeleteAttachment = this.onDeleteAttachment.bind(this);
  }

  ngOnInit() {
    this.restBaseUrl = this.authService.getActiveApplicationRestUrl();
    this.authToken = this.authService.getToken();
    this.referentId = this.authService.getActualProject().referent ? this.authService.getActualProject().referent.id : null;
    this.userId = this.authService.getUser().id;
  }

  canUploadFiles() {
    return this.authService.hasPermissionOnProject('manipulate_files') && this.userId === this.referentId;
  }

  onFileSelect(files: UploadFileExtended[]) {
    if (files.length === 1) {
      const dialog = this.dialogService.open(AttachmentChooseTypeFormComponent, {
        data: {
          allowType: this.allowType,
          attachmentCategory: this.attachmentCategory,
          file: files[0],
          defaultAttachmentType: this.defaultAttachmentType,
          disabledAttachmentTypes: this.disabledAttachmentTypes
        },
        className: ClassName.HIGHER_DIALOG,
      });

      const sub = dialog.afterClosed.subscribe((result: { type: string, note: string, baseName: string } | boolean) => {
        if (typeof result === 'object') {
          files.forEach(file => this.insertFileToUpload(file, result.type, result.note, result.baseName));
        } else {
          this.dokumenty.splice(0, this.dokumenty.length);
          files.splice(0, files.length);
        }
        sub.unsubscribe();
      });
    } else {
      for (const file of files.filter(file => !file.uploading)) {
        this.insertFileToUpload(file);
      }
    }
  }

  onEditAttachment(dokumentAdditionalData: DokumentAdditionalDataModel) {
    const dialog = this.dialogService.open(AttachmentEditFormComponent, {
      data: {
        allowType: this.allowType,
        omitFileUpload: true,
        attachment: {...dokumentAdditionalData},
        attachmentCategory: this.attachmentCategory,
        disabledAttachmentTypes: this.disabledAttachmentTypes
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: AttachmentModel) => {
      if (result) {
        dokumentAdditionalData.baseName = result.baseName;
        dokumentAdditionalData.attachmentType = result.type;
        dokumentAdditionalData.note = result.note;

        this.updated.emit(true);
      }
      sub.unsubscribe();
    });
  }

  onDeleteAttachment(index: number) {
    const dialog = this.dialogService.open(ConfirmationComponent, {
      data: {
        msg: 'Opravdu chcete soubor smazat?',
      },
      className: ClassName.ADJUSTED_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: boolean) => {
      if (result) {
        this.dokumenty.splice(index, 1);
        this.dokumentyAdditionalData.splice(index, 1);
        this.updated.emit(true);
      }
      sub.unsubscribe();
    });
  }

  getIconClass(dokumentAdditionalData: DokumentAdditionalDataModel): string {
    return `mr-10 fa fa-lg flex-icon ${this.fileToUploadTypePipe.transform(dokumentAdditionalData, 'icon')}`;
  }

  private insertFileToUpload(file: UploadFileExtended, attachmentType = '', note = '', baseName?) {
    this.dokumenty.push(file.fileEntry);

    this.dokumentyAdditionalData.push({
      filename: file.fileEntry.name,
      baseName: baseName,
      extension: file.extension,
      note: note,
      attachmentType: attachmentType
    });
  }
}
